import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { RadioButton } from 'primeng/radiobutton';
import { Server } from '#enums';
import { Dtos } from '#models';
import { UserSearchControl } from '../user-search-control/user-search-control';
import { ExternalUserForm } from '../external-user-form/external-user-form';
import { SharedUserForm } from '../shared-user-form/shared-user-form';
import { distinctUntilChanged, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-user-form',
  imports: [
    CommonModule,
    TranslatePipe,
    RadioButton,
    UserSearchControl,
    ExternalUserForm,
    SharedUserForm,
    ReactiveFormsModule,
    FormsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserForm),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UserForm),
      multi: true,
    },
  ],
  templateUrl: './user-form.html',
  styleUrl: './user-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserForm implements ControlValueAccessor, Validator, OnInit {
  private _destroyRef = inject(DestroyRef);

  UserType = Server.UserType;
  options = input.required<Server.UserType[]>();

  isReadOnly = input(false);
  showUserTypeLabel = input(true);
  showUserTypeSelector = input(true);
  externalActionTemplate = input<TemplateRef<unknown> | null>(null);

  readonly defaultSelectedUser = {
    ID: 0,
    ChildUsers: [] as Dtos.SharedUser[],
    Email: '',
    FirstName: '',
    LastName: '',
    Type: this.UserType.Chevron,
  } as Dtos.User;

  validate = (): ValidationErrors | null => {
    const selectedOption = this.userForm.controls['selectedOption'];
    const userKey = this.getUserKeyByType(selectedOption?.value);
    const selectedUser = this.userForm.controls[userKey];

    if (selectedOption?.value === this.UserType.Chevron) {
      return selectedUser?.value?.Email ? null : { required: true };
    }
    const errors = selectedUser?.errors;
    return errors as ValidationErrors | null;
  };

  userForm = new FormGroup({
    selectedOption: new FormControl<Server.UserType>(Server.UserType.Chevron),

    chevronUser: new FormControl<Dtos.User>(this.defaultSelectedUser),
    externalUser: new FormControl<Dtos.User>(this.defaultSelectedUser),
    sharedUser: new FormControl<Dtos.User>(this.defaultSelectedUser),
  });

  isChevronUserInvalid() {
    const selectedOption = this.userForm.controls['selectedOption'];
    if (selectedOption?.value !== this.UserType.Chevron) {
      return false;
    }
    const chevronUser = this.userForm.controls['chevronUser'];

    if (!chevronUser.dirty && !chevronUser.touched) {
      return false;
    }

    if (chevronUser?.value?.Email) {
      return false;
    }

    const userValue = chevronUser.value;
    if (typeof userValue === 'string') {
      const textValue: string = userValue;
      if (textValue.trim()) {
        return false;
      }
    }

    return true;
  }

  ngOnInit() {
    const [option = this.UserType.Chevron] = this.options();

    this.userForm.patchValue({ selectedOption: option }, { emitEvent: false }); //selectedOption.set(option);
  }

  writeValue(obj: Dtos.User | null): void {
    const user = obj === null ? this.defaultSelectedUser : obj;
    const userKey = this.getUserKeyByType(user.Type);
    this.userForm.patchValue(
      { [userKey]: user, selectedOption: user.Type || Server.UserType.Chevron },
      { emitEvent: false }
    );
    this.userForm.updateValueAndValidity({ emitEvent: false });
    // Reset touched/dirty so validation messages don't show after a programmatic reset
    this.userForm.markAsUntouched();
    this.userForm.markAsPristine();
  }
  getUserKeyByType(type: Server.UserType | null) {
    switch (type) {
    case Server.UserType.External:
      return 'externalUser';
    case Server.UserType.Shared:
      return 'sharedUser';
    default:
      return 'chevronUser';
    }
  }
  registerOnChange(fn: (value: Dtos.User) => void): void {
    this.userForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
        filter(val => !!val && 'object' === typeof val)
      )
      .subscribe(userForm => {
        const { selectedOption = null } = userForm;
        const userKey = this.getUserKeyByType(selectedOption);
        const selectedUser = userForm[userKey] as Dtos.User;
        const payload = {
          ...selectedUser,
          Type: selectedOption,
        } as Dtos.User;
        fn(payload);
      });
  }
  registerOnTouched(fn: () => void): void {
    this.userForm.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.userForm.touched) {
        fn();
      }
    });
  }

  selectedOptionChanged($event: { value: Server.UserType }): void {
    const userKey = this.getUserKeyByType($event.value);
    const selectedUser = this.userForm.get(userKey);
    if (!selectedUser) {
      return;
    }
    selectedUser?.setValue({
      ...this.defaultSelectedUser,
      Type: $event.value,
    });
    selectedUser?.markAsTouched();
    selectedUser.updateValueAndValidity();
  }

  formatUser = input((val: Dtos.User | string | null) => {
    // Just return the value as-is since it already has the correct type
    return val;
  });
}
